<script setup>
import { ref, watch } from 'vue'
import { InvoiceStatus, InvoiceType } from '../types'
import MultiSelect from './ui/MultiSelect.vue'
import { XIcon } from './Icons'
import { getInvoiceTypeLabel } from '../constants.js'


const props = defineProps({
	isOpen: Boolean,
	filterState: {
		type: Object,
		required: true,
	},
})

const emit = defineEmits(['close', 'apply', 'reset'])

const localState = ref({ ...props.filterState })

watch(
	() => props.isOpen,
	(open) => {
		if (open) {
			localState.value = { ...props.filterState }
		}
	}
)

const apply = () => {
	emit('apply', { ...localState.value })
	emit('close')
}

const reset = () => {
	const empty = {
		dateFrom: '',
		dateTo: '',
		statuses: [],
		types: [],
		amountMin: '',
		amountMax: '',
		clientName: '',
		phone: '',
	}
	localState.value = empty
	emit('reset')
	emit('close')
}

const handleEnter = (e) => {
	if (e.key === 'Enter') {
		e.preventDefault()
		apply()
	}
}

const inputClass =
	'w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm ' +
	'focus:outline-none focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500'
</script>

<template>
	<div
		v-if="isOpen"
		@keydown="handleEnter"
		@click.stop
		class="absolute top-12 left-0 z-40 w-80 bg-white border border-slate-200 rounded-lg shadow-2xl ring-1 ring-slate-900/5"
	>
		<!-- Header -->
		<div class="flex items-center justify-between p-4 border-b bg-slate-50/50 rounded-t-lg">
			<h3 class="font-semibold text-slate-900">
				{{ $t('invoices.filters') }}
			</h3>
			<button @click="emit('close')" class="text-slate-400 hover:text-slate-600">
				<XIcon class="w-5 h-5" />
			</button>
		</div>

		<div class="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
			<div class="space-y-2">
				<label class="text-xs font-bold text-slate-500 uppercase tracking-wide">
					{{ $t('invoices.filter_date_created') }}
				</label>
				<div class="grid grid-cols-2 gap-2">
					<div class="relative">
						<input
							type="date"
							v-model="localState.dateFrom"
							:class="inputClass"
						/>
					</div>
					<div class="relative">
						<input
							type="date"
							v-model="localState.dateTo"
							:class="inputClass"
						/>
					</div>
				</div>
			</div>

			<!-- STATUS -->
			<div class="space-y-2">
				<label class="text-xs font-bold text-slate-500 uppercase tracking-wide">
					{{ $t('invoices.filter_status') }}
				</label>
				<MultiSelect
					:options="Object.values(InvoiceStatus)"
					:selected="localState.statuses"
					@change="val => localState.statuses = val"
					:labelFn="s => $t(`invoices.status_${s}`) || s"
					:placeholder="$t('invoices.filter_select_placeholder')"
				/>
			</div>

			<!-- TYPE -->
			<div class="space-y-2">
				<label class="text-xs font-bold text-slate-500 uppercase tracking-wide">
					{{ $t('invoices.filter_type') }}
				</label>
				<MultiSelect
					:options="Object.values(InvoiceType)"
					:selected="localState.types"
					@change="val => localState.types = val"
					:labelFn="tpe => getInvoiceTypeLabel(tpe, $t)"
					:placeholder="$t('invoices.filter_select_placeholder')"
				/>
			</div>

			<!-- AMOUNT -->
			<div class="space-y-2">
				<label class="text-xs font-bold text-slate-500 uppercase tracking-wide">
					{{ $t('invoices.filter_amount') }}
				</label>
				<div class="grid grid-cols-2 gap-2">
					<input
						type="number"
						v-model="localState.amountMin"
						:placeholder="$t('invoices.filter_amount_from')"
						:class="inputClass"
					/>
					<input
						type="number"
						v-model="localState.amountMax"
						:placeholder="$t('invoices.filter_amount_to')"
						:class="inputClass"
					/>
				</div>
			</div>

			<!-- CLIENT -->
			<div class="space-y-2">
				<label class="text-xs font-bold text-slate-500 uppercase tracking-wide">
					{{ $t('invoices.filter_client') }}
				</label>
				<input
					type="text"
					v-model="localState.clientName"
					:class="inputClass"
				/>
			</div>

			<!-- PHONE -->
			<div class="space-y-2">
				<label class="text-xs font-bold text-slate-500 uppercase tracking-wide">
					{{ $t('invoices.filter_phone') }}
				</label>
				<input
					type="text"
					v-model="localState.phone"
					:class="inputClass"
				/>
			</div>
		</div>

		<!-- Footer -->
		<div class="p-4 border-t bg-slate-50/50 flex gap-2 rounded-b-lg">
			<button
				@click="apply"
				class="flex-1 bg-slate-600 hover:bg-slate-600 text-white py-2 rounded-md text-sm font-medium"
			>
				{{ $t('invoices.filter_apply') }}
			</button>
			<button
				@click="reset"
				class="flex-1 bg-white hover:bg-slate-50 text-slate-700 py-2 rounded-md text-sm font-medium border border-slate-200"
			>
				{{ $t('invoices.filter_reset') }}
			</button>
		</div>
	</div>
</template>
